import { useService } from '@Client/runner.hooks/useService';
import React from 'react';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IProcessMap from '@Shared.Angular/@types/processMap';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

const ProcessDetails = (props: IProcessMap) => {
  const {
    Id,
    editableByCurrentUser,
    processOwner,
    category,
    processReviewDate,
    description,
    objective,
    background,
    processInput,
    processOutput,
    isPublicMapView,
    triggerInput,
    totalActiveTimeHours,
    totalWaitingTimeHours,
    totalCalculatedCost,
    totalTimeHours
  } = props;

  const tempModelerUrlService = useService<SharedAngular.TempModelerUrlService>(
    'tempModelerUrlService'
  );
  const permissionsService =
    useService<SharedAngular.PermissionsService>('permissionsService');
  const flowinglyConstants =
    useService<SharedAngular.FlowinglyConstants>('flowinglyConstants');
  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');

  const updateViewModelEdit = (): boolean => {
    const hasPermission = permissionsService.currentUserHasPermission(
      flowinglyConstants.permissions.FLOWMODEL_UPDATE
    );
    return hasPermission || editableByCurrentUser;
  };

  const onEditFlowModelHandler = () => {
    tempModelerUrlService.openModeler({ flowModelId: Id });
  };

  const typographyWithEmptyString = (text: string, emptyString: string) => {
    if (text) {
      return <div className="text-content">{text}</div>;
    } else {
      return <div className="text-content empty">{emptyString}</div>;
    }
  };

  const formatDate = (dateString: string) => {
    if (dateString == null) {
      return null;
    }
    const date = new Date(dateString);
    const day = date.toLocaleString('en-US', { day: '2-digit' });
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.toLocaleString('en-US', { year: 'numeric' });
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="process-map-view-v2-left-panel-process-detail-container">
      <div className="process-map-view-v2-left-panel-process-detail-grid">
        <div>
          <label className="row-title">Category</label>
          {typographyWithEmptyString(category, '')}
        </div>
        <div>
          <label className="row-title">Process Owner</label>
          {typographyWithEmptyString(
            processOwner,
            'Process Owner not yet defined'
          )}
        </div>
        <div>
          <label className="row-title">Process Review Date</label>
          {typographyWithEmptyString(
            formatDate(processReviewDate),
            'Process Review Date not yet defined'
          )}
        </div>
        <div>
          <label className="row-title">Description</label>
          <div>{description}</div>
        </div>
        <div>
          <label className="row-title">Trigger</label>
          {typographyWithEmptyString(
            triggerInput,
            'Trigger input not yet defined'
          )}
        </div>
        <div>
          {appConfig.enableTimeCost && (
            <div className="process-map-view-v2-left-panel-process-detail-time-cost-box">
              <label className="row-title">Time and Cost Analysis</label>
              <div className="process-map-view-v2-left-panel-process-detail-time-cost-row">
                <label>Active Time:</label>
                <div>{totalActiveTimeHours} hrs</div>
              </div>
              <div className="process-map-view-v2-left-panel-process-detail-time-cost-row">
                <div>Waiting Time:</div>
                <div>{totalWaitingTimeHours} hrs</div>
              </div>
              <div className="divider" />
              <div className="process-map-view-v2-left-panel-process-detail-time-cost-row">
                <div>Total Time:</div>
                <div>{totalTimeHours} hrs</div>
              </div>
              <div className="divider" />
              <div className="process-map-view-v2-left-panel-process-detail-time-cost-row">
                <div>
                  Total Cost{' '}
                  <i
                    title="Total cost is calculated as: Total Active Time × Average Cost Per Hour"
                    className="fa-light fa-circle-info"
                  ></i>
                  :
                </div>
                <div>${totalCalculatedCost}</div>
              </div>
            </div>
          )}
        </div>
        <div className="col-full">
          <label className="row-title">Objective</label>
          {typographyWithEmptyString(objective, 'Objective not yet defined')}
        </div>
        <div className="col-full">
          <label className="row-title">Background</label>
          {typographyWithEmptyString(background, 'Background not yet defined')}
        </div>
        <div>
          <label className="row-title">Process Input</label>
          {typographyWithEmptyString(
            processInput,
            'Process Input not yet defined'
          )}
        </div>
        <div>
          <label className="row-title">Process Output</label>
          {typographyWithEmptyString(
            processOutput,
            'Process Output not yet defined'
          )}
        </div>
        {!isPublicMapView && (
          <div className="col-full process-detail-buttons">
            {updateViewModelEdit() && (
              <TextButton
                type="secondary"
                text="Edit Flow Model"
                onClick={onEditFlowModelHandler}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessDetails;
